import React, { useEffect, useRef, useState } from 'react';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    secondaryLight: '#ffd97c',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

const sections = [
    {
        imageLeft: true,
        tag: 'La primera impresión importa',
        title: 'La sala donde tus ideas se convierten en decisiones',
        body: 'Hay reuniones que definen el rumbo de un negocio. La presentación ante un cliente clave, la negociación de una alianza estratégica, la sesión donde tu equipo toma la decisión más importante del año. Esas reuniones merecen un escenario a la altura.\n\nDesde 2019, nuestra Sala Summit ha sido testigo de más de 500 reuniones que cambiaron el rumbo de empresas bolivianas. No es exageración — es el resultado de diseñar un espacio donde cada detalle comunica profesionalismo: la acústica perfecta, la iluminación cálida pero funcional, la tecnología que simplemente funciona cuando más la necesitas.',
        features: [
            'Capacidad para 6, 12 o 20 personas según la sala',
            'Pantalla 4K con conexión HDMI, USB-C y wireless',
            'Sistema de videoconferencia Zoom/Teams integrado',
            'Pizarra digital interactiva incluida',
        ],
        accent: colors.secondary,
        gradient: `linear-gradient(145deg, #004d7a, ${colors.secondary})`,
        icon: '🎯',
        stat: '500+',
        statLabel: 'Reuniones realizadas',
    },
    {
        imageLeft: false,
        tag: 'El problema que todos conocemos',
        title: 'Basta de reuniones en cafés ruidosos o videollamadas que fallan',
        body: '¿Cuántas veces has tenido que pedir disculpas porque el internet falló, porque el ruido del fondo era insoportable, o porque simplemente no tenías un lugar adecuado para recibir a tus clientes? Cada vez que eso pasa, tu marca pierde credibilidad.\n\nLas salas de reunión de Co Marca eliminan ese problema de raíz. Con reserva desde 1 hora, acceso inmediato el mismo día, y tecnología que funciona sin configuraciones complicadas, llegás a tu reunión enfocado en lo que importa: cerrar el trato, alinear al equipo, o presentar tu mejor trabajo.',
        features: [
            'Reserva online en menos de 2 minutos',
            'Disponibilidad desde las 7:00 hasta las 22:00',
            'Cancelación gratuita hasta 2 horas antes',
            'Catering y coffee break a pedido',
        ],
        accent: colors.primary,
        gradient: `linear-gradient(145deg, ${colors.primaryDark}, ${colors.primary})`,
        icon: '⚡',
        stat: '2 min',
        statLabel: 'Tiempo de reserva online',
    },
    {
        imageLeft: true,
        tag: 'Tecnología sin fricciones',
        title: 'Enchufa y presenta. Sin cables que no encajan ni drivers que faltan',
        body: 'Nada arruina más rápido el momentum de una reunión que pasar los primeros 15 minutos intentando conectar una laptop a la pantalla. Hemos diseñado nuestras salas pensando exactamente en eso: tecnología de nivel corporativo pero con la simplicidad que exige el mundo real.\n\nConéctate por cable HDMI, USB-C o de forma completamente inalámbrica desde cualquier dispositivo Windows, Mac, iOS o Android. Y si tu reunión es híbrida, nuestra cámara con seguimiento inteligente de voz hace que quienes están del otro lado de la pantalla se sientan dentro de la sala.',
        features: [
            'Cámara inteligente 4K con tracking automático de voz',
            'Micrófonos omnidireccionales con cancelación de eco',
            'Compatible con Google Meet, Zoom, Teams y Webex',
            'Soporte técnico presencial inmediato ante cualquier duda',
        ],
        accent: colors.accentDark,
        gradient: `linear-gradient(145deg, #7a4800, ${colors.accentDark})`,
        icon: '🖥️',
        stat: '100%',
        statLabel: 'Plug & Play garantizado',
    },
    {
        imageLeft: false,
        tag: 'Flexibilidad para cada ocasión',
        title: 'Desde una entrevista uno a uno hasta un directorio completo',
        body: 'No todas las reuniones son iguales, y el espacio debe adaptarse al momento. Contamos con 4 configuraciones distintas de salas: desde espacios íntimos para entrevistas o sesiones de coaching de 2 a 4 personas, hasta nuestra Sala Directorio con capacidad para 20 personas en mesa ejecutiva.\n\nTodas las salas incluyen servicio de recepción para tus invitados, café y agua de cortesía, y la posibilidad de solicitar servicio de catering personalizado para jornadas largas de trabajo.',
        features: [
            'Sala Entrevista: 2–4 personas',
            'Sala Equipo: 6–8 personas',
            'Sala Ejecutiva: 10–12 personas',
            'Sala Directorio / Summit: hasta 20 personas',
        ],
        accent: '#8b3fa8',
        gradient: 'linear-gradient(145deg, #005a4b, #8b3fa8)',
        icon: '👥',
        stat: '4',
        statLabel: 'Tipos de sala según necesidad',
    },
];

// ── Split Section Card con BorderGlow ──
function SplitCard({ section, index }: { section: typeof sections[0]; index: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.08);

    const imgBlock = (
        <div style={{
            flex: '0 0 440px', minHeight: '380px',
            position: 'relative', overflow: 'hidden',
            background: section.gradient,
        }}>
            <img
                src={`/images/salas/sala-${index + 1}.jpg`}
                alt={section.title}
                style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.6s cubic-bezier(.16,1,.3,1)',
                }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            {/* Overlay gradient fallback */}
            <div style={{
                position: 'absolute', inset: 0, background: section.gradient,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px',
            }}>
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', background: 'rgba(255,255,255,0.07)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', bottom: '-50px', left: '-30px', width: '220px', height: '220px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%' }} />
                <div style={{ fontSize: '5rem', filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.2))' }}>{section.icon}</div>
                <div style={{
                    background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.25)', borderRadius: '16px',
                    padding: '14px 28px', textAlign: 'center',
                }}>
                    <div style={{ fontSize: '2.2rem', fontWeight: '900', color: '#fff', lineHeight: 1 }}>{section.stat}</div>
                    <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)', fontWeight: '600', marginTop: '4px' }}>{section.statLabel}</div>
                </div>
            </div>
            {/* Accent bar */}
            <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px',
                background: `linear-gradient(90deg, ${section.accent}, ${section.accent}88)`,
            }} />
        </div>
    );

    const textBlock = (
        <div style={{
            flex: 1, minWidth: '280px', padding: '52px 48px',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: section.accent }}>
                    {section.tag}
                </span>
                <div style={{ height: '2px', width: '32px', borderRadius: '2px', background: section.accent }} />
            </div>
            <h2 style={{
                fontSize: 'clamp(1.4rem, 2.4vw, 1.9rem)', fontWeight: '800',
                color: isDark ? '#ffffff' : '#0f172a',
                lineHeight: '1.2', letterSpacing: '-0.02em', marginBottom: '20px',
                fontFamily: "'DM Sans', sans-serif",
            }}>
                {section.title}
            </h2>
            {section.body.split('\n\n').map((para, pi) => (
                <p key={pi} style={{
                    fontSize: '0.95rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85',
                    marginBottom: pi < section.body.split('\n\n').length - 1 ? '16px' : '28px',
                }}>
                    {para}
                </p>
            ))}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {section.features.map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: isDark ? '#e2e8f0' : '#374151', fontWeight: '500' }}>
                        <span style={{
                            width: '20px', height: '20px', borderRadius: '50%',
                            background: `${section.accent}20`, color: section.accent,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.75rem', fontWeight: '800', flexShrink: 0,
                        }}>✓</span>
                        {f}
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <div
            ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(36px)',
                transition: 'all 0.45s cubic-bezier(.16,1,.3,1)',
                transitionDelay: `${index * 0.06}s`,
            }}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
            >
                <article
                    style={{
                        display: 'flex',
                        flexDirection: section.imageLeft ? 'row' : 'row-reverse',
                        width: '100%',
                        flexWrap: 'wrap',
                    }}
                >
                    {section.imageLeft ? <>{imgBlock}{textBlock}</> : <>{textBlock}{imgBlock}</>}
                </article>
            </BorderGlow>
        </div>
    );
}

// ── Hero ──
function HeroSection() {
    const { isDark } = useTheme();
    const [headerRef, headerInView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => { const t = setTimeout(() => setForced(true), 100); return () => clearTimeout(t); }, []);
    const visible = headerInView || forced;

    return (
        <section style={{
            background: isDark
                ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #faf6fb 55%, #f6ecf5 100%)',
            padding: 'clamp(90px, 12vw, 130px) 32px clamp(50px, 7vw, 70px)',
            position: 'relative', overflow: 'hidden',
        }}>
            <div className="blob-anim" style={{ position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px', background: isDark ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))' : 'linear-gradient(135deg, rgba(97,37,123,0.12), rgba(193,52,139,0.08))', pointerEvents: 'none' }} />
            {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
            <HexagonCornerDecoration
                variant="horizontal-degrade"
                direction="left-to-right"
                opacity={isDark ? 0.70 : 0.50}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />

            <div ref={headerRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                <h1 className={`fade-up-svc d1 ${visible ? 'in' : ''}`} style={{ fontSize: 'clamp(2.4rem, 5vw, 3.8rem)', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.08', letterSpacing: '-0.025em', marginBottom: '12px' }}>
                    Salas de <span style={{ background: 'linear-gradient(135deg, #61257b 0%, #c1348b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Reunión</span>
                </h1>
                <h2 className={`fade-up-svc d1 ${visible ? 'in' : ''}`} style={{ fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: '600', color: isDark ? '#cbd5e1' : '#475569', letterSpacing: '-0.01em', marginBottom: '20px' }}>
                    Donde las grandes <span style={{ color: colors.secondary }}>decisiones toman forma</span>
                </h2>
                <p className={`fade-up-svc d2 ${visible ? 'in' : ''}`} style={{ fontSize: '1.05rem', color: isDark ? '#94a3b8' : '#64748b', maxWidth: '560px', margin: '0 auto', lineHeight: '1.75', fontWeight: '400' }}>
                    Salas profesionales equipadas con tecnología de última generación en Calacoto, La Paz. Reserva por horas, sin contratos, sin complicaciones — solo resultados.
                </p>
                {/* Stats */}
                <div className={`fade-up-svc d3 ${visible ? 'in' : ''}`} style={{ display: 'flex', justifyContent: 'center', gap: '0', marginTop: '40px', flexWrap: 'wrap' }}>
                    {[
                        { value: '500+', label: 'Reuniones realizadas', color: colors.primary },
                        { value: '4', label: 'Salas disponibles', color: colors.secondary },
                        { value: '4.9★', label: 'Calificación promedio', color: colors.accentDark },
                    ].map((stat, i) => (
                        <div key={stat.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 36px', borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9') : 'none' }}>
                            <div style={{ fontSize: '2.6rem', fontWeight: '900', color: stat.color, lineHeight: 1, letterSpacing: '-0.03em' }}>{stat.value}</div>
                            <div style={{ fontSize: '0.82rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '6px' }}>{stat.label}</div>
                        </div>
                    ))}
                </div>
                <div className={`fade-up-svc d3 ${visible ? 'in' : ''}`} style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes variant="scroll-prompt" size="md" promptText="Explorar salas" />
                </div>
            </div>
        </section>
    );
}

// ── CTA Banner ──
function CtaBanner() {
    return (
        <div style={{
            marginTop: '64px', borderRadius: '28px',
            background: `linear-gradient(135deg, #004d7a 0%, ${colors.secondary} 60%, ${colors.secondaryLight} 100%)`,
            padding: '56px 48px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '28px',
            position: 'relative', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(97, 37, 123, 0.35)',
        }}>
            <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '280px', height: '280px', background: 'rgba(255,255,255,0.06)', borderRadius: '50%' }} />
            <div style={{ position: 'relative' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFD97C', marginBottom: '10px' }}>¿Tienes una reunión importante?</div>
                <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: '800', color: '#fff', lineHeight: '1.15', letterSpacing: '-0.02em' }}>Reserva tu sala ahora mismo</h3>
                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', marginTop: '10px', maxWidth: '460px', lineHeight: '1.7' }}>
                    Disponibilidad en tiempo real. Reserva en 2 minutos y llega a tu reunión con todo listo para impresionar.
                </p>
            </div>
            <a href="/contacto" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: '#fff', color: colors.secondary, borderRadius: '14px', padding: '14px 32px',
                fontWeight: '700', fontSize: '0.97rem', textDecoration: 'none', flexShrink: 0,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', transition: 'all 0.22s',
            }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; }}
            >
                Reservar sala →
            </a>
        </div>
    );
}

// ── Main ──
const ServicesSalaReu: React.FC = () => {
    const { isDark } = useTheme();
    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: isDark ? '#0b0614' : '#f8fafc', minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim { animation: blobFloat 9s ease-in-out infinite; }
        .fade-up-svc { opacity: 0; transform: translateY(28px); transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1); }
        .fade-up-svc.in { opacity: 1; transform: translateY(0); }
        .fade-up-svc.d1 { transition-delay: 0.1s; }
        .fade-up-svc.d2 { transition-delay: 0.2s; }
        .fade-up-svc.d3 { transition-delay: 0.3s; }
        @media (max-width: 900px) {
          article { flex-direction: column !important; }
          article > div:first-child, article > div:last-child { flex: none !important; width: 100% !important; min-height: 260px !important; }
        }
      `}</style>
            <HeroSection />
            <section style={{ padding: '60px 32px 100px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    {sections.map((section, index) => (
                        <SplitCard key={index} section={section} index={index} />
                    ))}
                </div>
                <CtaBanner />
            </section>
        </div>
    );
};

export default ServicesSalaReu;